import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { isOrderable, normalizeQuantity } from "@/lib/product";
import { useAuth } from "@/context/AuthContext";

const CART_STORAGE_KEY = "shop_cart";
const CartContext = createContext(null);

function readCart() {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

/**
 * Client-side cart only (briefing §2: geen persistentie nodig).
 * We keep a small product snapshot so the cart can render names/prices
 * without extra API calls; quantities may be decimal.
 */
export function CartProvider({ children }) {
  const [lines, setLines] = useState(readCart);
  const { account } = useAuth();

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(lines));
    } catch (e) {
      /* ignore */
    }
  }, [lines]);

  const addLine = useCallback((product, aantal = 1) => {
    const qty = normalizeQuantity(product, aantal);
    setLines((prev) => {
      const existing = prev.find((l) => l.product_id === product.id);
      const snapshot = {
        id: product.id,
        sku: product.sku,
        naam: product.naam,
        omschrijving: product.omschrijving,
        eenheid: product.eenheid,
        btw_tarief: product.btw_tarief,
        verkoopprijs: product.verkoopprijs,
        consument_adviesprijs: product.consument_adviesprijs,
        beschikbaar: product.beschikbaar,
        land_actief: product.land_actief,
        op_voorraad: product.op_voorraad,
        voorraad: product.voorraad,
        leverancier_op_voorraad: product.leverancier_op_voorraad,
        is_klantprijs: product.is_klantprijs,
        hoofdfoto: product.hoofdfoto,
        besteleenheid: product.besteleenheid,
        minimale_afname: product.minimale_afname,
        vertalingen: product.vertalingen || {},
      };
      if (existing) {
        return prev.map((l) =>
          l.product_id === product.id
            ? { ...l, aantal: normalizeQuantity(snapshot, l.aantal + qty), product: snapshot }
            : l,
        );
      }
      return [...prev, { product_id: product.id, aantal: qty, product: snapshot }];
    });
  }, []);

  const setQuantity = useCallback((productId, aantal) => {
    const qty = Number(aantal);
    setLines((prev) =>
      prev.map((l) =>
        l.product_id === productId
          ? { ...l, aantal: Number.isFinite(qty) && qty > 0 ? qty : l.aantal }
          : l,
      ),
    );
  }, []);

  /** Aantal naar een hele verpakking afronden (aanroepen bij blur/bestellen). */
  const normalizeLine = useCallback((productId) => {
    setLines((prev) =>
      prev.map((l) =>
        l.product_id === productId ? { ...l, aantal: normalizeQuantity(l.product, l.aantal) } : l,
      ),
    );
  }, []);

  const removeLine = useCallback((productId) => {
    setLines((prev) => prev.filter((l) => l.product_id !== productId));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  /**
   * Verse CRM-data over de mandje-snapshots leggen (briefing feb-2026 §2c):
   * prijs, voorraad en de beschikbaarheids-toggle kunnen inmiddels gewijzigd zijn.
   */
  const refreshFromProducts = useCallback((producten) => {
    if (!Array.isArray(producten) || producten.length === 0) return;
    const perId = new Map(producten.map((p) => [p.id, p]));
    setLines((prev) =>
      prev.map((l) => {
        const fresh = perId.get(l.product_id);
        if (!fresh) return l;
        return {
          ...l,
          product: {
            ...l.product,
            naam: fresh.naam,
            sku: fresh.sku,
            eenheid: fresh.eenheid,
            btw_tarief: fresh.btw_tarief,
            verkoopprijs: fresh.verkoopprijs,
            consument_adviesprijs: fresh.consument_adviesprijs,
            beschikbaar: fresh.beschikbaar,
            land_actief: fresh.land_actief,
            op_voorraad: fresh.op_voorraad,
            voorraad: fresh.voorraad,
            leverancier_op_voorraad: fresh.leverancier_op_voorraad,
            is_klantprijs: fresh.is_klantprijs,
            hoofdfoto: fresh.hoofdfoto,
            besteleenheid: fresh.besteleenheid,
            minimale_afname: fresh.minimale_afname,
            vertalingen: fresh.vertalingen || {},
          },
        };
      }),
    );
  }, []);

  const value = useMemo(() => {
    const totalExcl = lines.reduce(
      (sum, l) => sum + Number(l.product?.verkoopprijs || 0) * Number(l.aantal || 0),
      0,
    );
    const itemCount = lines.length;
    const blockedLines = lines.filter((l) => !isOrderable(l.product, account));
    return {
      lines,
      itemCount,
      totalExcl,
      blockedLines,
      hasBlockedLines: blockedLines.length > 0,
      addLine,
      setQuantity,
      normalizeLine,
      removeLine,
      refreshFromProducts,
      clear,
    };
  }, [lines, account, addLine, setQuantity, normalizeLine, removeLine, refreshFromProducts, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
